// Designer document channel client (Pilar F, F2). Subscribes to a
// dashboard's op stream over WS so edits by other clients — including the
// chat/LLM — appear live on the canvas. Auth uses a short-lived embed
// token (minted via show_dashboard). Reconnects with backoff.

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export interface DocOpsMessage {
  type: 'ops';
  dashboard_id: string;
  ops: unknown[];
  rev: number;
  by?: string;
  client_id?: string | null;
}

/** Ephemeral presence (cursor + selection) relayed between peers (F5.3). */
export interface PresenceMessage {
  type: 'presence';
  from: string;
  by?: string;
  color: string;
  x?: number;
  y?: number;
  selection?: string | null;
  label?: string;
}
export interface PresenceLeaveMessage { type: 'presence_leave'; from: string }
/** A whole-document restore (snapshot revert, F5.4) — clients reload. */
export interface RestoreMessage { type: 'restore'; dashboard_id: string; rev: number; by?: string }
/** A live shared-chat message, scoped to a session (kind: a full message,
 *  a "typing" indicator, or the end of a turn). */
export interface ChatLiveImage { mediaType: string; dataBase64: string }
export interface ChatMessage {
  type: 'chat';
  from: string;
  by?: string;
  session_id?: string;
  kind?: 'message' | 'typing' | 'end';
  role: 'user' | 'assistant';
  text?: string;
  images?: ChatLiveImage[];
}
export type ChatSend = Omit<ChatMessage, 'type' | 'from' | 'by'>;

export interface DocChannel {
  close(): void;
  /** Send my cursor/selection to peers (no-op until the socket is open). */
  sendPresence(p: { x?: number; y?: number; selection?: string | null; label?: string }): void;
  /** Broadcast a live shared-chat message to the room. */
  sendChat(m: ChatSend): void;
}

export function connectDocChannel(opts: {
  token: string;
  docId: string;
  onOps: (msg: DocOpsMessage) => void;
  onStatus?: (s: 'open' | 'closed') => void;
  onPresence?: (msg: PresenceMessage) => void;
  onPresenceLeave?: (msg: PresenceLeaveMessage) => void;
  onRestore?: (msg: RestoreMessage) => void;
  onChat?: (msg: ChatMessage) => void;
}): DocChannel {
  const wsBase = (apiBase || window.location.origin).replace(/^http/, 'ws');
  const url = `${wsBase}/api/designer/live?token=${encodeURIComponent(opts.token)}&doc=${encodeURIComponent(opts.docId)}`;
  let ws: WebSocket | null = null;
  let closed = false;
  let backoff = 1000;

  const open = (): void => {
    if (closed) return;
    ws = new WebSocket(url);
    ws.onopen = () => {
      backoff = 1000;
      opts.onStatus?.('open');
    };
    ws.onmessage = (e) => {
      try {
        const m = JSON.parse(String(e.data));
        if (!m) return;
        if (m.type === 'ops') opts.onOps(m as DocOpsMessage);
        else if (m.type === 'presence') opts.onPresence?.(m as PresenceMessage);
        else if (m.type === 'presence_leave') opts.onPresenceLeave?.(m as PresenceLeaveMessage);
        else if (m.type === 'restore') opts.onRestore?.(m as RestoreMessage);
        else if (m.type === 'chat') opts.onChat?.(m as ChatMessage);
      } catch {
        /* ignore malformed */
      }
    };
    ws.onclose = () => {
      opts.onStatus?.('closed');
      if (!closed) {
        setTimeout(open, backoff);
        backoff = Math.min(backoff * 2, 15000);
      }
    };
    ws.onerror = () => ws?.close();
  };
  open();

  return {
    close(): void {
      closed = true;
      ws?.close();
    },
    sendPresence(p): void {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'presence', ...p }));
    },
    sendChat(m): void {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'chat', ...m }));
    },
  };
}

/** Extract the embed JWT from a show_dashboard `url` (…?token=<jwt>). */
export function tokenFromShowUrl(url: string): string | null {
  try {
    return new URL(url, window.location.origin).searchParams.get('token');
  } catch {
    return null;
  }
}
